import React, { useState, useEffect, useMemo, useRef, useCallback, Suspense } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ErrorBoundary from "./ErrorBoundary";
import PropertyList, { PropertyListHandle } from "./PropertyList";
import SearchAutocomplete from "./SearchAutocomplete";
import { resolveDisplayStatus } from "../lib/dataQuality";
import {
  SortField,
  SORT_FIELDS,
  DEFAULT_SORT_FIELD,
  sortDirTooltip,
  sortProperties,
} from "../lib/propertySort";
import { isVacantLand } from "../lib/propertyUrls";
import SkeletonCard from "./SkeletonCard";
import AuthModal from "./AuthModal";
import PropertyModal from "./PropertyModal";
import UserMenu from "./UserMenu";
import NotificationBell from "./NotificationBell";
import SettingsPanel from "./SettingsPanel";
import UpgradePrompt from "./UpgradePrompt";
import CountyPickerModal from "./CountyPickerModal";
import AttributionPrompt from "./AttributionPrompt";
import OnboardingQuestionnaire from "./OnboardingQuestionnaire";
import SetupCallModal from "./SetupCallModal";
import { UpgradeReasonCode } from "../lib/upgradeReasons";
import LeadsView from "./LeadsView";
import CountySelector from "./CountySelector";
import LockedPropertyCard from "./LockedPropertyCard";
import LandingPage from "./LandingPage";
import FeedbackButton from "./FeedbackButton";
import ProductTour from "./ProductTour";
import DataSourceFooter from "./DataSourceFooter";
import { Property, PropertyRow, County, LockedProperty, isLockedProperty } from "../types";
import { getCounties, getProperty, getProfileCounties, downloadPropertiesCSV, resetCountiesCache, DEFAULT_COUNTY_ID } from "../api";
import { useAuth } from "../hooks/useAuth";
import { useToast } from "../hooks/useToast";
import { useCountyProperties } from "../hooks/useCountyProperties";
import { clearPropertiesCache } from "../lib/propertiesCache";
import { useWatchlist, WatchlistCapInfo } from "../hooks/useWatchlist";
import { useHiddenProperties } from "../hooks/useHiddenProperties";
import { DEAL_STAGES, dealStageLabel } from "../lib/dealStages";
import { routeLinks, routableCount, STOPS_PER_LINK } from "../lib/routeLink";
import { MapArea, filterToArea } from "../lib/mapArea";
import BulkActionBar from "./BulkActionBar";
import FollowUpsDue from "./FollowUpsDue";
import { useChanges } from "../hooks/useChanges";
import AuctionSheet from "./AuctionSheet";
import { createHoverStore } from "../lib/hoverStore";
import { DealStage } from "../lib/dealStages";
import { exportErrorMessage } from "../lib/exportErrors";
import { apiClient } from "../lib/apiClient";
import { capture, setUserProperties } from "../lib/analytics";
import { MapBuyBox, EMPTY_MAP_BUY_BOX, applyMapBuyBox, mapBuyBoxActiveCount } from "../lib/mapBuyBox";
import { matchOwnerTypes } from "../lib/ownerType";
import { planOpenNav, planCloseNav } from "../lib/modalHistory";
import { lazyWithRetry } from "../lib/lazyWithRetry";
import { getPlanCapabilities, SCOUT_BUY_BOX_LIMIT, UserPlan } from "../lib/planCapabilities";
import { resolveSuggestedCounty, getEmptyCountyNotice, resolveViewableCounty } from "../lib/countyOnboarding";
import {
  clearPendingIntent,
  isValidCountySlug,
  isValidPropertyId,
  parseIntentParam,
  planIntentIsUpgrade,
  readPendingIntent,
  savePendingIntent,
  BillingInterval,
  PaidPlanIntent,
} from "../lib/pendingIntent";
import {
  clearCountyPickerSnooze,
  deepLinkAuthMode,
  dismissPickCountyBanner,
  isCountyPickerSnoozed,
  isPickCountyBannerDismissed,
  shouldAutoOpenCountyPicker,
  shouldShowPickCountyBanner,
  snoozeCountyPicker,
} from "../lib/onboardingFlow";
import { trackConfirmedSignupLanding } from "../lib/signupFunnel";
import {
  BILLING_POLL_INTERVAL_MS,
  BILLING_POLL_MAX_ATTEMPTS,
  billingReturnKind,
  checkoutSettledMessage,
  parseCheckoutReturn,
  pollPlan,
  stripBillingParams,
} from "../lib/billingReturn";
import ResumeCheckoutPrompt from "./ResumeCheckoutPrompt";
import PaymentIssueBanner from "./PaymentIssueBanner";
import { computeAvailableSignalTypes } from "../lib/leadTypeMeta";
import "../App.css";

// Heavy components are lazy-loaded so they land in separate webpack chunks.
// ForeclosureMap is the biggest win: it carries the entire @arcgis/core SDK
// (~2-3 MB compressed) that users who don't navigate to the map never need.
// PortfolioView is gated behind Pro + 3-watched, so most sessions never load
// it either. lazyWithRetry (not bare React.lazy) so a stale chunk after a
// deploy triggers a one-time reload instead of crashing the route — see
// lib/lazyWithRetry.ts.
const ForeclosureMap = lazyWithRetry(() => import("./map"));
const PortfolioView = lazyWithRetry(() => import("./PortfolioView"));

// localStorage key for the user's last-selected county. Renamed from the
// legacy "gavl.countyId" during the Parcely rebrand. The IIFE below is a
// one-time migration so existing users don't lose their saved county;
// safe to delete after a few months once stragglers have migrated.
const COUNTY_STORAGE_KEY = "parcely.countyId";
(() => {
  try {
    const legacy = localStorage.getItem("gavl.countyId");
    if (legacy && !localStorage.getItem(COUNTY_STORAGE_KEY)) {
      localStorage.setItem(COUNTY_STORAGE_KEY, legacy);
      localStorage.removeItem("gavl.countyId");
    }
  } catch {
    /* private browsing — ignore */
  }
})();

type ViewMode = "all" | "watchlist" | "portfolio" | "leads";
// Minimum watchlist entries required to access the Portfolio tab.
// Keeps the dashboard meaningful — charts look broken with 1 property.
const PORTFOLIO_MIN_WATCHED = 3;
// #1378: below this many total properties, a county's list reads as
// "near-empty" — 1-3 cards with nothing below them, no framing. 17 active
// counties sit at exactly 1 property today (verified 2026-08-09), and at
// that size freeTierLimit === totalPropertyCount (nothing is locked), so
// the existing "X of Y unlocked" banner never fires — this is the gap it
// leaves. Threshold is deliberately about the county's real inventory, not
// plan-derived, since a thin county reads the same way on every plan.
const SMALL_COUNTY_THRESHOLD = 10;
// Sort field defs + comparator live in lib/propertySort (2026-09: split the flat
// "score_desc / assessed_desc / …" list into a field choice + a direction toggle
// on PropertyList's list header, and made the comparator unit-testable). Shared
// by both All Properties and My Watchlist (the two tabs that render PropertyList).

function Dashboard() {
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  // #782: when AddToWatchlist 403s for exceeding the plan's watchlist cap
  // (#780), useWatchlist reports it here instead of a generic error toast.
  // The effect below (next to showUpgrade/upgradeReason) turns it into the
  // same UpgradePrompt modal every other Pro-gate uses.
  const [watchlistCapNotice, setWatchlistCapNotice] = useState<WatchlistCapInfo | null>(null);
  const handleWatchlistCapExceeded = useCallback((info: WatchlistCapInfo) => {
    setWatchlistCapNotice(info);
  }, []);
  const {
    watchedIds,
    entries: watchEntries,
    toggle: toggleWatch,
    togglingIds,
    getNotes,
    updateNotes,
    getStage,
    setStage,
    addMany: addManyToWatchlist,
  } = useWatchlist(handleWatchlistCapExceeded);
  // Deal stages (#3075): the watchlist tab filters on them and shows a pill.
  const [watchlistStage, setWatchlistStage] = useState<string>("");
  const stageLabels = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of watchEntries) {
      const label = dealStageLabel(e.stage);
      if (label) m.set(e.propertyId, label);
    }
    return m;
  }, [watchEntries]);
  // "Not interested" (#3074): hidden properties leave the All Properties map
  // and list unless the user asks to see them; the watchlist ignores hiding.
  const { hiddenIds, toggle: toggleHidden, hideMany } = useHiddenProperties();
  // Bulk select (#3079): select mode, the checked ids, and an in-flight flag.
  const [selectMode, setSelectMode] = useState(false);
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const toggleChecked = useCallback((id: string) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const [showHidden, setShowHidden] = useState(false);
  // Printable auction-week sheet (#3211).
  const [auctionSheetOpen, setAuctionSheetOpen] = useState(false);
  // List <-> map hover sync (#3077): the card or pin under the pointer. A
  // store, not state, so a hover doesn't re-render the Dashboard (see
  // lib/hoverStore). One instance for the component's life.
  const [hoverStore] = useState(createHoverStore);
  const location = useLocation();
  const navigate = useNavigate();
  // Keep location in a ref so handleSelectProperty doesn't need it in
  // its useCallback deps (avoids recreating the function on every URL
  // change, which would bust React.memo on ForeclosureMap).
  const locationRef = useRef(location);
  locationRef.current = location;

  // Bumped to force a property refetch when something OUTSIDE this effect's
  // natural deps changes what the server would return — today only the #1465
  // resolved-rows display toggle, which is applied server-side, so the loaded
  // rows go stale the instant it flips.
  const [propertiesReloadKey, setPropertiesReloadKey] = useState(0);
  const [selectedLocked, setSelectedLocked] = useState<LockedProperty | null>(null);
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [propertyClassFilter, setPropertyClassFilter] = useState("");
  // "" = All, "auction" = live auctions only (inAuction), "lead" = pre-auction
  // leads only (!inAuction). leadTypeFilter narrows further by
  // leadSignals[].signalType and only makes sense alongside "lead" (or "").
  const [inAuctionFilter, setInAuctionFilter] = useState<"" | "auction" | "lead">("");
  // Checked lead types: a property matches if it carries ANY of them. [] = all.
  const [leadTypeFilter, setLeadTypeFilter] = useState<string[]>([]);
  // "Changed since my last visit" (#3207): filter the list to properties with a change.
  const [changedOnly, setChangedOnly] = useState(false);
  // Owner kinds (#3205): absentee, company, out of state, estate. Any checked type matches.
  const [ownerTypeFilter, setOwnerTypeFilter] = useState<string[]>([]);
  // Vacant-land-only toggle (#1604). Client-side, same as every other filter
  // here — the county's full row set is already in memory (visibleProperties
  // below), so there's no reason to round-trip the backend for this. Uses
  // isVacantLand (lib/propertyUrls.ts), NOT property.propertyClass —
  // propertyClass="land_only" is only meaningfully populated in Mecklenburg
  // and Guilford (checked live 2026-08-19), so it would silently under-filter
  // everywhere else.
  const [vacantOnly, setVacantOnly] = useState(false);
  // Map buy box (#3061): building facts + value, unknowns kept by default.
  const [mapBuyBox, setMapBuyBox] = useState<MapBuyBox>(EMPTY_MAP_BUY_BOX);
  // My Watchlist's own lightweight search-within-saved-set (2026-09
  // tab-chrome redesign). Deliberately separate state from `searchQuery` —
  // that one drives the All Properties discovery bar and must NOT leak
  // into Watchlist (a stale status/class/auction filter left over from
  // browsing would otherwise silently hide watched properties with no bar
  // visible to explain why). See filteredProperties below: the discovery
  // filters only apply when viewMode === "all"; this one only when
  // viewMode === "watchlist".
  const [watchlistQuery, setWatchlistQuery] = useState("");
  // Default sort is Deal Score — the list should answer "where are the
  // opportunities?" first, not "what's most expensive?" (2026-08-21 UX pass;
  // every other sort remains one select away).
  const [sortField, setSortField] = useState<SortField>(DEFAULT_SORT_FIELD);
  const [sortReversed, setSortReversed] = useState(false);
  // Transient "map cluster focus": when the user clicks a clustered group
  // of pins on the map, we narrow the list pane to exactly those member
  // ids (Option A — reaches co-located pins that zooming can't separate).
  // null = no cluster focus. Cleared automatically when any other
  // filter/search/county changes (see the effect below) or via the
  // banner's Clear button.
  const [mapFocusIds, setMapFocusIds] = useState<string[] | null>(null);
  // "Search this area" / drawn area (#3078). Unlike a cluster focus it is a
  // shape, not a list of ids, so it survives filter changes (list = filters
  // ∩ area) and narrows the map's pins as well as the list.
  const [mapArea, setMapArea] = useState<MapArea | null>(null);
  // County selection is initialized from localStorage (last user choice)
  // and reconciled with the live county list once it loads. We seed
  // with DEFAULT_COUNTY_ID as a string fallback so the first /properties
  // fetch always has a value — even before getCounties() resolves —
  // avoiding a no-op skeleton flicker on cold load.
  //
  // A county landing page's "Browse these leads" links to /app?county=<id>
  // (it used to be a bare /app, which landed everyone on Mecklenburg). That
  // explicit request wins over the stored county for the first render.
  const [countyId, setCountyId] = useState<string>(() => {
    const requested = new URLSearchParams(location.search).get("county");
    if (isValidCountySlug(requested)) {
      try { localStorage.setItem(COUNTY_STORAGE_KEY, requested); } catch { /* private browsing */ }
      return requested;
    }
    try {
      return localStorage.getItem(COUNTY_STORAGE_KEY) || DEFAULT_COUNTY_ID;
    } catch {
      return DEFAULT_COUNTY_ID;
    }
  });
  // The county a visitor explicitly arrived for (landing-page link, possibly
  // carried across email confirmation by lib/pendingIntent). It becomes the
  // county picker's suggestion ahead of the watchlist/browsing fallbacks.
  const [requestedCounty, setRequestedCounty] = useState<string | null>(() => {
    const requested = new URLSearchParams(location.search).get("county");
    return isValidCountySlug(requested) ? requested : null;
  });
  const [counties, setCounties] = useState<County[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>("all");
  const [tourReplayNonce, setTourReplayNonce] = useState(0);
  // Notification bell (#2302) → Leads: which saved buy box to apply on arrival.
  // `n` makes a repeat click on the same box a fresh request.
  const [leadsBoxRequest, setLeadsBoxRequest] = useState<{ id: string; n: number } | undefined>(undefined);

  // Whole-county CSV for SearchAutocomplete's Export CSV control (paid
  // tiers, "all properties" view only). Same blob -> <a download> hand-off
  // LeadsView uses; SearchAutocomplete owns the busy/error/menu state, so
  // this just throws on failure.
  const handleExportCsv = useCallback(
    async (addedWithinDays: number | null) => {
      const blob = await downloadPropertiesCSV(countyId, addedWithinDays ?? undefined);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      const range = addedWithinDays ? `new-${addedWithinDays}d-` : "";
      a.download = `parcely-${countyId}-${range}${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      capture("export_clicked", {
        type: "properties",
        county_id: countyId,
        added_within_days: addedWithinDays,
      });
    },
    [countyId]
  );

  // Bulk actions (#3079). Each acts on the checked rows the list still shows.
  const BULK_MAX = 500; // matches the export's ?ids= cap
  const bulkIds = useCallback(
    () => Array.from(checkedIds).slice(0, BULK_MAX),
    [checkedIds]
  );
  const finishBulk = useCallback((action: string, count: number) => {
    capture("bulk_action", { action, count });
    setCheckedIds(new Set());
  }, []);
  const handleBulkExport = useCallback(async () => {
    const ids = bulkIds();
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      const blob = await downloadPropertiesCSV(countyId, undefined, ids);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `parcely-${countyId}-selected-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      finishBulk("export", ids.length);
    } catch (err) {
      toast(exportErrorMessage(err, "Export failed. Please try again."), "error");
    } finally {
      setBulkBusy(false);
    }
  }, [bulkIds, countyId, finishBulk, toast]);

  // Tier set extended in #121: scout / pro / team join the legacy
  // free / admin. `isPro`, `isUnrestrictedPlan`, `canUseLeads`, `leadsTier`,
  // and `watchLimitMax` below all come from getPlanCapabilities(userPlan)
  // (lib/planCapabilities.ts) — that module is now the single documented
  // source of truth for what each plan gets and why (including the full
  // #677 audit of which per-tier caps had already shipped before this
  // model existed). Read it before changing any of these names' meaning.
  //
  // Genuinely still ungated (#677/#678): Team's 5 seats and API access, and
  // Scout's 25/mo ML quota. All three are blocked on features that do
  // not exist yet (no seat model, no API-key layer, no ML), not on gating
  // work. Both tiers remain comingSoon/non-purchasable in Pricing.tsx, so
  // nothing is currently sold that isn't enforced.
  const [userPlan, setUserPlan] = useState<UserPlan>("free");
  // userPlan defaults to "free" before /profile resolves; this tells "free" from
  // "not known yet" so a paid user is never treated as county-restricted.
  const [planLoaded, setPlanLoaded] = useState(false);

  // Geography-aware tier redesign (#864 backend / county-picker frontend).
  // chosenCounties drives isAllowedCounty below; countiesOnboardedAt (from
  // GET /profile/counties) gates whether the one-time CountyPickerModal
  // auto-opens — null means the picker has never been shown/dismissed for
  // this user. countiesProfileLoaded distinguishes "still loading" from
  // "loaded and genuinely null" so the picker-open effect doesn't fire on
  // the initial render before the fetch resolves.
  const [chosenCounties, setChosenCounties] = useState<string[]>([]);
  const [countiesOnboardedAt, setCountiesOnboardedAt] = useState<string | null>(null);
  const [countiesProfileLoaded, setCountiesProfileLoaded] = useState(false);
  const [showCountyPicker, setShowCountyPicker] = useState(false);
  // Skip / backdrop / Escape on the picker. A dismiss used to end onboarding
  // forever (the modal stamped counties_onboarded_at on mount); now it only
  // keeps the picker shut for this page session, plus a localStorage snooze
  // (lib/onboardingFlow) so it comes back on a later visit.
  const [pickerDismissedThisSession, setPickerDismissedThisSession] = useState(false);
  const [pickBannerDismissed, setPickBannerDismissed] = useState(false);
  // Shown when the post-checkout poll times out before the webhook lands.
  const [billingNotice, setBillingNotice] = useState<string | null>(null);
  // A plan the visitor picked on /pricing before signing up (lib/pendingIntent).
  const [resumeCheckout, setResumeCheckout] = useState<{
    plan: PaidPlanIntent;
    interval: BillingInterval;
    variant?: "signup" | "checkout_cancel";
  } | null>(null);

  // UI modals
  const [showAuth, setShowAuth] = useState(false);
  // Which AuthModal tab to open on. The hard-wall "Get started" CTA opens
  // signup; "Sign in" / ?auth=login opens signin (#617).
  const [authMode, setAuthMode] = useState<"signin" | "signup">("signin");
  const [showSettings, setShowSettings] = useState(false);
  // "Book a 15-minute setup call" (migration 246). Holds the request's source
  // (day0_email, winback_email, user_menu) while the form is open.
  const [setupCallSource, setSetupCallSource] = useState<string | null>(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState<string | undefined>();
  // Which paywall opened the prompt (lib/upgradeReasons) — the copy above is
  // for the user, this is for the funnel. Always set together via openUpgrade.
  const [upgradeReasonCode, setUpgradeReasonCode] = useState<UpgradeReasonCode>("unknown");
  const openUpgrade = useCallback((code: UpgradeReasonCode, reason?: string) => {
    setUpgradeReasonCode(code);
    setUpgradeReason(reason);
    setShowUpgrade(true);
  }, []);
  // "How did you hear about us?" — shown once after the county picker.
  const [signupSourceAskedAt, setSignupSourceAskedAt] = useState<string | null | undefined>(undefined);
  // Required questionnaire (migration 261) for NEW free accounts. It replaces
  // the skippable prompt for them; older accounts keep the prompt below.
  // Fail-open on outage via onContinueWithoutSaving (session only).
  const [onboardingRequired, setOnboardingRequired] = useState(false);
  const [onboardingBypassed, setOnboardingBypassed] = useState(false);
  const showOnboarding =
    !!user &&
    planLoaded &&
    countiesProfileLoaded &&
    onboardingRequired &&
    !onboardingBypassed &&
    userPlan === "free" &&
    !showUpgrade &&
    !resumeCheckout;
  const showAttribution =
    !!user &&
    planLoaded &&
    countiesProfileLoaded &&
    signupSourceAskedAt === null &&
    !onboardingRequired &&
    !showCountyPicker &&
    !showUpgrade &&
    !resumeCheckout;
  const pendingUpgrade = useRef(false);

  // #782: turn a real watchlist-cap 403 (see handleWatchlistCapExceeded
  // above) into the same UpgradePrompt modal the client-side pre-check
  // uses, but with the server's actual message.
  //
  // Kept as defense-in-depth even though watchLimitMax now mirrors the
  // server for every tier: the server is the source of truth, and this
  // path still covers drift between the two, a stale client bundle, or a
  // plan that changed mid-session.
  //
  // (This comment used to say the pre-check missed scout because the
  // backend capped scout at the same 3 as free. That is no longer true —
  // the tier redesign gave scout its own cap of 10 on BOTH sides:
  // SCOUT_WATCH_LIMIT here and watchlistCap() in
  // backend/internal/handlers/watchlist_handler.go.)
  //
  // The effect itself lives below planCapabilities: it has to know whether
  // there is a higher plan to offer (see hasHigherPlan).

  // Tracks whether opening the property modal pushed a browser-history
  // entry (#501). True after an in-app open (map/list click) so closing
  // pops that entry; false for a deep-link open (`?property=` already in
  // the URL on load) so closing just strips the param.
  const didPushModalRef = useRef(false);

  const listRef = useRef<PropertyListHandle>(null);

  // Sentry canary (#117 follow-up). Visiting /?_test=sentry on a
  // build with REACT_APP_SENTRY_DSN configured will throw a labelled
  // error after a 100ms tick, which the root ErrorBoundary catches
  // and forwards to Sentry. Used to verify end-to-end Sentry capture
  // + sourcemap upload + Slack alert routing in prod without waiting
  // for a real bug.
  //
  // Gated on the explicit query param so it can NEVER fire from a
  // bookmark, an external link, or an accidental keystroke. The 100ms
  // delay puts the throw outside the React render path so it's
  // caught by the boundary, not tied to a render error in this
  // component.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("_test") !== "sentry") return;
    // eslint-disable-next-line no-console
    console.warn("[sentry-canary] firing test error in 100ms — see Sentry dashboard");
    const t = setTimeout(() => {
      throw new Error("sentry-canary: deliberate test error from /?_test=sentry");
    }, 100);
    return () => clearTimeout(t);
  }, [location.search]);

  // Load the live county list once on mount. Reconcile with the user's
  // last-chosen county from localStorage: if the stored ID isn't in the
  // active list (e.g. county got disabled, or user is loading from a
  // device with a stale cache), fall through to the first active county.
  useEffect(() => {
    getCounties()
      .then((list) => {
        setCounties(list);
        if (list.length === 0) return;
        const stored = (() => { try { return localStorage.getItem(COUNTY_STORAGE_KEY); } catch { return null; } })();
        const found = stored && list.find((c) => c.id === stored);
        if (!found) {
          // Use first active county as canonical fallback. Active flag
          // is implicit — the API only returns active counties.
          setCountyId(list[0].id);
          try { localStorage.setItem(COUNTY_STORAGE_KEY, list[0].id); } catch { /* private browsing */ }
        }
      })
      .catch(() => {
        // Network / auth failure — leave countyId at its initial value
        // (DEFAULT_COUNTY_ID or whatever localStorage had). UI keeps
        // working against that one county.
        setCounties([]);
      });
  }, []);

  // Active County object (vs the bare string id) — passed to map so it
  // can read mapServerUrl, fieldMappings, etc. without a separate fetch.
  const activeCounty = useMemo(
    () => counties.find((c) => c.id === countyId) ?? null,
    [counties, countyId]
  );

  // The county's property set. useCountyProperties owns the fetch: it renders a
  // county this user already opened from an in-memory cache and revalidates in
  // the background, and drops a response whose county is no longer selected
  // (load-time fix, 2026-09-16). resp.properties is already the UNLOCKED rows
  // only (api.ts splits at the boundary), so every downstream consumer keeps
  // operating on Property[] and can't mistake a locked row for a real one.
  // lockedProperties feeds ONLY the map's grey layer, the per-zip counts and
  // the locked teaser card.
  const {
    properties,
    lockedProperties,
    mapPoints,
    mapLockedProperties,
    totalCount: totalPropertyCount,
    freeTierLimit,
    loading: propertiesLoading,
    error: propertiesError,
    errorInfo: propertiesErrorInfo,
  } = useCountyProperties({
    countyId,
    userId: user?.id,
    authLoading,
    reloadKey: propertiesReloadKey,
  });

  // #2257 — which lead-signal types the county's own already-fetched
  // properties actually carry, for SearchAutocomplete's "Lead Types"
  // dropdown. Deliberately derived from the FULL `properties` set (not
  // `filteredProperties`/`visibleProperties` below), so applying one filter
  // never narrows what other filter options the menu offers next.
  const availableLeadTypes = useMemo(() => computeAvailableSignalTypes(properties), [properties]);

  // Signing out drops every cached county response from memory: those rows
  // carry plan-gated owner data (see lib/propertiesCache.ts).
  useEffect(() => {
    if (!authLoading && !user) {
      clearPropertiesCache();
      resetCountiesCache();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, authLoading]);

  // Fetch user plan from the profile endpoint
  useEffect(() => {
    if (!user) {
      setUserPlan("free");
      setPlanLoaded(false);
      return;
    }
    apiClient
      .get<{ plan: UserPlan }>("/profile")
      .then(({ data }) => {
        const plan = data.plan || "free";
        setUserPlan(plan);
        // #133 — tag the PostHog person with their plan so funnels/cohorts
        // can break down by tier. Person-property update (not a re-identify).
        setUserProperties({ plan });
      })
      .catch(() => setUserPlan("free"))
      .finally(() => setPlanLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Fetch chosen-counties + onboarding state alongside the plan (geography-
  // aware tier redesign). Follow-up fetch rather than folded into the /profile
  // call above — keeps that endpoint's shape untouched and this feature's
  // fetch independently retry-able/cancelable.
  useEffect(() => {
    if (!user) {
      setChosenCounties([]);
      setCountiesOnboardedAt(null);
      setCountiesProfileLoaded(false);
      return;
    }
    getProfileCounties()
      .then((resp) => {
        setChosenCounties(resp.chosenCounties || []);
        setCountiesOnboardedAt(resp.countiesOnboardedAt);
        // undefined (older API) reads as "already asked": never nag on a
        // field the server does not know about.
        setSignupSourceAskedAt(resp.signupSourceAskedAt === undefined ? "unknown" : resp.signupSourceAskedAt);
        setOnboardingRequired(resp.onboardingRequired === true);
      })
      .catch(() => {
        setChosenCounties([]);
        setCountiesOnboardedAt(null);
        setSignupSourceAskedAt("unknown");
      })
      .finally(() => setCountiesProfileLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // #133 funnel event #3 — alert_click. Fires once on landing when the URL
  // carries alert-email UTM params. The email-side links that set these
  // (utm_medium=alert, utm_campaign=<alert_type>, property=<id>) are wired
  // backend-side in the alert dispatcher (PR2 / #118); this is the landing
  // capture that completes the funnel once those links ship.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isAlert = params.get("utm_medium") === "alert" || params.get("utm_source") === "email_alert";
    if (!isAlert) return;
    capture("alert_click", {
      alert_type: params.get("utm_campaign") || params.get("alert_type") || "unknown",
      property_id: params.get("property") || null,
    });
    // Mount-only — capture the inbound alert click exactly once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stripe return reconciliation (#89). Two return paths, see
  // lib/billingReturn.ts:
  //   ?checkout=success — Checkout finished; the plan arrives by webhook,
  //                       which can land after the redirect.
  //   ?billing=updated  — the Customer Portal's return URL (plan switch,
  //                       cancel, or just a card update).
  //
  // Poll /profile (2s, 30s cap) until the plan settles, then apply it AND
  // refetch the property rows: the server decides which rows come back
  // locked from the plan, so the rows already on screen were fetched under
  // the old plan. This used to update the plan only, which left the map and
  // cards locked until a manual reload. A checkout that times out tells the
  // user their access is still activating instead of leaving them on the
  // free view wondering whether the payment failed. The params are stripped
  // afterwards (keeping e.g. ?property=) so a reload does not re-poll.
  //
  // Waits for the first /profile load so "changed" has a real baseline: a
  // portal return compares against the plan the app loaded with.
  const userPlanRef = useRef<UserPlan>(userPlan);
  userPlanRef.current = userPlan;
  useEffect(() => {
    const kind = billingReturnKind(location.search);
    if (!kind || !user || !planLoaded) return;

    let cancelled = false;
    const baseline = userPlanRef.current;
    const pathname = location.pathname;
    const search = location.search;
    const bought = parseCheckoutReturn(search);

    // Left Stripe without paying. Record it (funnel: checkout_cancelled),
    // clear the params, and offer to pick the purchase back up: until
    // 2026-10-19 the buyer landed on the map with no message at all.
    if (kind === "cancel") {
      capture("checkout_cancelled", { plan: bought.plan, interval: bought.interval, trial: bought.trial });
      navigate(stripBillingParams(pathname, search), { replace: true });
      if (bought.plan && planIntentIsUpgrade(baseline, bought.plan)) {
        setResumeCheckout({ plan: bought.plan, interval: bought.interval ?? "monthly", variant: "checkout_cancel" });
      }
      return;
    }

    pollPlan({
      fetchPlan: async () => {
        const { data } = await apiClient.get<{ plan: UserPlan }>("/profile");
        return data.plan || "free";
      },
      isSettled: kind === "checkout" ? (plan) => plan !== "free" : (plan) => plan !== baseline,
      isCancelled: () => cancelled,
      maxAttempts: BILLING_POLL_MAX_ATTEMPTS,
      intervalMs: BILLING_POLL_INTERVAL_MS,
    }).then((result) => {
      if (cancelled) return;
      if (kind === "checkout") {
        // Client-side twin of the backend's trial_started/upgrade_completed:
        // this one knows whether the user actually came back and how long
        // the webhook took to land (attempts × 2s).
        capture("checkout_completed", {
          plan: result.plan ?? bought.plan,
          interval: bought.interval,
          trial: bought.trial,
          settled: result.settled,
          attempts: result.attempts,
        });
      }
      if (result.settled && result.plan) {
        setUserPlan(result.plan);
        setUserProperties({ plan: result.plan });
        clearPropertiesCache();
        setPropertiesReloadKey((k) => k + 1);
        // A portal return that changed the plan ends quietly; a checkout
        // return says what just happened (trial vs paid).
        setBillingNotice(
          kind === "checkout" ? checkoutSettledMessage({ settled: true, trial: bought.trial, plan: bought.plan }) : null,
        );
      } else if (kind === "checkout") {
        setBillingNotice(checkoutSettledMessage({ settled: false, trial: bought.trial, plan: bought.plan }));
      }
      // A portal visit that changed nothing (card update, cancel at period
      // end) ends quietly.
      navigate(stripBillingParams(pathname, search), { replace: true });
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, location.search, planLoaded]);

  // /settings and /settings/alerts are the "manage" links every alert and
  // lifecycle email points at (alerts/templates.go prefsURL, the trial-ending
  // email). No route ever handled them, so the click landed on the plain map.
  // Open the Settings panel instead; a signed-out visitor signs in first and
  // lands in Settings once the session resolves.
  useEffect(() => {
    if (!location.pathname.startsWith("/settings")) return;
    if (authLoading) return;
    if (!user) {
      setShowAuth(true);
      return;
    }
    setShowSettings(true);
    navigate(`/${location.search}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, user?.id, authLoading]);

  // /setup-call?source=<where> is the link in the day-0 and win-back emails.
  // Open the form for a signed-in user (sign in first otherwise), then drop
  // the path so a reload does not reopen it.
  useEffect(() => {
    if (!location.pathname.startsWith("/setup-call")) return;
    if (authLoading) return;
    if (!user) {
      setShowAuth(true);
      return;
    }
    const params = new URLSearchParams(location.search);
    setSetupCallSource(params.get("source") || "app");
    params.delete("source");
    const qs = params.toString();
    navigate(qs ? `/?${qs}` : "/", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, user?.id, authLoading]);

  // Funnel events that can be joined to the user (#133): email_confirmed and
  // signup_completed fire on the first authenticated load after
  // /auth/callback. useAuth has already called identify by the time `user`
  // is set here.
  useEffect(() => {
    if (user) trackConfirmedSignupLanding(user.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Resume what a visitor was doing before signup (lib/pendingIntent.ts).
  // Email confirmation opens a NEW tab at /auth/callback -> /app, with none
  // of the original query params; the intent survives in localStorage.
  //   plan     -> the "Continue to checkout" prompt (never an auto-charge)
  //   redeem   -> back to /redeem/<code>
  //   property -> reopen the deep-linked property
  //   county   -> start on that county and suggest it in the picker
  // Cleared once used. Runs once per signed-in user, after the plan is known
  // (a plan intent is only offered when it is an upgrade).
  const resumedForUserRef = useRef<string | null>(null);
  useEffect(() => {
    if (!user || !planLoaded) return;
    if (resumedForUserRef.current === user.id) return;
    resumedForUserRef.current = user.id;

    // A signed-in user who arrives with ?intent= (e.g. signed in on the
    // signup modal opened from /pricing) is treated the same way.
    const params = new URLSearchParams(location.search);
    const urlIntent = parseIntentParam(params.get("intent"));
    if (urlIntent) savePendingIntent({ intent: urlIntent });

    const stored = readPendingIntent();
    if (!stored) return;
    clearPendingIntent();

    if (stored.countyId) {
      setRequestedCounty(stored.countyId);
      setCountyId(stored.countyId);
      try { localStorage.setItem(COUNTY_STORAGE_KEY, stored.countyId); } catch { /* ignore */ }
    }
    const intent = stored.intent;
    if (!intent) return;
    capture("intent_resumed", { kind: intent.kind });
    if (intent.kind === "redeem") {
      navigate(intent.code ? `/redeem/${encodeURIComponent(intent.code)}` : "/redeem");
    } else if (intent.kind === "property") {
      if (!params.has("property")) {
        const next = new URLSearchParams(location.search);
        next.set("property", intent.propertyId);
        navigate(`${location.pathname}?${next.toString()}`, { replace: true });
      }
    } else if (planIntentIsUpgrade(userPlan, intent.plan)) {
      setResumeCheckout({ plan: intent.plan, interval: intent.interval });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, planLoaded]);

  // Once signed in, the one-shot entry params have done their job (the
  // county was adopted at init or by the resume above, the intent saved):
  // strip them so a reload or a shared URL does not replay them.
  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(location.search);
    const requested = params.get("county");
    if (!params.has("county") && !params.has("intent") && !params.has("auth")) return;
    if (isValidCountySlug(requested)) {
      setRequestedCounty(requested);
      setCountyId(requested);
      try { localStorage.setItem(COUNTY_STORAGE_KEY, requested); } catch { /* ignore */ }
    }
    if (!planLoaded) return; // let the resume effect read ?intent= first
    params.delete("county");
    params.delete("intent");
    params.delete("auth");
    const qs = params.toString();
    navigate(qs ? `${location.pathname}?${qs}` : location.pathname, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, location.search, planLoaded]);

  // Auto-reopen upgrade modal after sign-in if user was trying to subscribe
  useEffect(() => {
    if (user && pendingUpgrade.current) {
      pendingUpgrade.current = false;
      setShowUpgrade(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // On sign-out, close any lingering modals and clear the pendingUpgrade
  // flag. Without this, a sequence like (click Upgrade while signed-out →
  // sign in as Pro → sign out) can leave `showUpgrade=true` in state,
  // which then shows the upgrade modal to the newly-anonymous user as
  // soon as the `!isPro` guard flips back to true.
  useEffect(() => {
    if (!user) {
      setShowUpgrade(false);
      setShowSettings(false);
      pendingUpgrade.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const authed = !!user;
  // What changed in this county since the user's last visit (#3207); signed-in only.
  const { kindsById: changeKinds, count: changesCount } = useChanges(countyId, authed);
  // getPlanCapabilities (lib/planCapabilities.ts) is the single source of
  // truth for what each plan gets — see that module for the full #677
  // audit of what's already correctly tiered vs. what's deliberately left
  // coarse. Recomputed only when userPlan changes.
  const planCapabilities = useMemo(() => getPlanCapabilities(userPlan), [userPlan]);
  // Any paid tier. Deliberately coarse — see planCapabilities.ts for which
  // features this should and shouldn't gate.
  const isPro = planCapabilities.isPro;
  // Seller Leads (owner contacts + CSV export) is the premium wedge — gated
  // to every paid tier the backend /leads endpoint allows. Scout gets the
  // same full rows as Pro, limited to its chosen counties; pro/team/admin
  // are statewide — see leadsTier below and the backend's leads handler.
  const canUseLeads = planCapabilities.canUseLeads;
  const leadsTier = planCapabilities.leadsTier;
  // Buy-box email deep link (#3062): /?box=<id> opens Leads with that saved
  // box applied, the same request the bell makes. Sign in first; dropped
  // once handled so a reload doesn't replay it.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const boxId = params.get("box");
    if (!boxId) return;
    if (authLoading) return;
    if (!user) {
      setShowAuth(true);
      return;
    }
    if (!planLoaded) return;
    if (canUseLeads) {
      setLeadsBoxRequest({ id: boxId, n: Date.now() });
      setViewMode("leads");
    }
    params.delete("box");
    const qs = params.toString();
    navigate(qs ? `${location.pathname}?${qs}` : location.pathname, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, user?.id, authLoading, planLoaded, canUseLeads]);
  // Scout gets its own watchlist cap (10) — distinct from Pro's effectively-
  // unlimited cap and Free's 3. Matches watchlistCap on the backend
  // (watchlist_handler.go) for the Scout tier introduced alongside the
  // geography-aware access gate (#864).
  const watchLimitMax = planCapabilities.watchlistLimit;
  // Whether there is a higher plan to sell (free/scout yes, pro/team/admin
  // no). The UpgradePrompt used to be gated on !isPro, and isPro includes
  // Scout, so every Scout paywall (watchlist cap, locked card, out-of-area
  // field, "no subscription found") opened nothing at all.
  const hasHigherPlan = planCapabilities.hasHigherPlan;

  // The watchlist-cap 403 (see handleWatchlistCapExceeded above). A plan with
  // something to upgrade to gets the UpgradePrompt with the server's own
  // message; a plan without one (Pro's cap is a sentinel nobody should reach)
  // still gets told why the save failed instead of silence.
  useEffect(() => {
    if (!watchlistCapNotice) return;
    if (hasHigherPlan) {
      openUpgrade("watchlist_cap", watchlistCapNotice.message);
    } else {
      toast(watchlistCapNotice.message, "error");
    }
    setWatchlistCapNotice(null);
  }, [watchlistCapNotice, hasHigherPlan, toast, openUpgrade]);

  // isAllowedCounty — geography-aware tier gate (Free/Scout redesign, #864).
  // Mirrors the backend's fullAccessAllowed (geo_access.go): Pro/team/admin
  // see full data everywhere; Free/Scout see full data only inside their
  // chosen counties. Deliberately does NOT reuse `isPro` here — `isPro`
  // (above) is a broad "paying customer" flag that already includes
  // "scout", so `isPro || chosenCounties.includes(...)` would make this
  // always true for Scout regardless of county, defeating the fuzzing
  // entirely for that tier. Free is unaffected either way since `isPro`
  // correctly excludes it — this only matters for Scout.
  const isUnrestrictedPlan = planCapabilities.isUnrestrictedPlan;
  // Stripe Customer Portal from the avatar menu. UserMenu only renders the
  // item for paying tiers (isPro), so the "no subscription found" branch is
  // a safety net, not the expected path — it mirrors SettingsPanel's twin
  // handler (openBillingPortal there), which stays the canonical copy with
  // its own loading state. Before this, a paying user's dropdown was a
  // dead end: UserMenu supported onManageSubscription but Dashboard never
  // passed it.
  // "No subscription found" from the portal endpoint. The old copy assumed
  // Free; a Scout (or a plan granted without Stripe) saw either the wrong
  // text or, through the !isPro gate, nothing at all.
  const showNoSubscription = useCallback(() => {
    if (hasHigherPlan) {
      openUpgrade(
        "no_subscription",
        userPlan === "free"
          ? "You're on the Free plan — there's no subscription to manage yet. Upgrade to unlock full county coverage, Leads, and more."
          : "There's no Stripe subscription on this account to manage (your plan was set up directly). Upgrade to Pro for statewide coverage, or email support@parcelyapp.com with billing questions."
      );
    } else {
      toast(
        "There's no Stripe subscription on this account to manage — your plan was set up directly. Email support@parcelyapp.com with billing questions.",
        "info"
      );
    }
  }, [hasHigherPlan, userPlan, toast, openUpgrade]);

  const openBillingPortal = useCallback(async () => {
    try {
      const { data } = await apiClient.post<{ url: string }>("/billing/portal");
      window.location.href = data.url;
    } catch (err: any) {
      if (err?.response?.data?.error === "no subscription found") {
        showNoSubscription();
        return;
      }
      alert(err?.response?.data?.error || "Could not open billing portal");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPlan, hasHigherPlan]);

  // Keep a free/scout viewer on a county they may list. The stored/default
  // county can fall outside their chosen set (a new user picking a county in
  // the picker, or a returning one with a stale localStorage county) and
  // GET /properties 403s `county_not_in_plan` — surfaced as "Request failed
  // with status code 403". Waits for both the plan and the chosen counties.
  useEffect(() => {
    if (!user || !planLoaded || !countiesProfileLoaded) return;
    const viewable = resolveViewableCounty({ countyId, isUnrestrictedPlan, chosenCounties });
    if (viewable !== countyId) {
      setCountyId(viewable);
      try { localStorage.setItem(COUNTY_STORAGE_KEY, viewable); } catch { /* ignore */ }
    }
  }, [user, planLoaded, countiesProfileLoaded, countyId, isUnrestrictedPlan, chosenCounties]);

  // The effect above needs /profile AND /profile/counties first, but the
  // property fetch starts the moment auth resolves — so the stored/default
  // county's 403 can land before them (or they can fail), and the list showed
  // "Error: Request failed with status code 403" (2026-10-13, a new free user
  // whose only county was alamance-nc). The 403 body carries the counties this
  // viewer may list: that is server truth, so move to the first of them
  // straight away, and treat the moment until that county loads as loading,
  // not as an error.
  const countyScopeRedirect = useMemo(() => {
    if (propertiesErrorInfo?.kind !== "county_not_in_plan") return null;
    const target = resolveViewableCounty({
      countyId,
      isUnrestrictedPlan: false,
      chosenCounties: propertiesErrorInfo.chosenCounties ?? [],
    });
    return target !== countyId ? target : null;
  }, [propertiesErrorInfo, countyId]);
  useEffect(() => {
    if (!countyScopeRedirect) return;
    setCountyId(countyScopeRedirect);
    try { localStorage.setItem(COUNTY_STORAGE_KEY, countyScopeRedirect); } catch { /* ignore */ }
    // /profile/counties failed or has not answered: adopt the server's list so
    // the switcher and gates agree with what the API enforces.
    const serverChosen = propertiesErrorInfo?.chosenCounties ?? [];
    if (serverChosen.length > 0) {
      setChosenCounties((prev) => (prev.length === 0 ? serverChosen : prev));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countyScopeRedirect]);
  const loading = propertiesLoading || countyScopeRedirect !== null;
  const error = countyScopeRedirect !== null ? null : propertiesError;

  const isAllowedCounty = useCallback(
    (property: Property): boolean => isUnrestrictedPlan || chosenCounties.includes(property.countyId),
    [isUnrestrictedPlan, chosenCounties]
  );

  // Suggested county for the picker: the county the user has the most
  // watchlist items in, if any. Uses Map#forEach (not for...of) — this
  // target doesn't have downlevelIteration enabled for Map iteration.
  //
  // #1377: a brand-new signup has an empty watchlist by construction (you
  // can't watch a property before you have an account), so `best` below is
  // almost always undefined for exactly the users this modal exists to
  // welcome. Falling back to `countyId` — the currently-viewed county,
  // seeded from localStorage and populated identically for anonymous and
  // signed-in sessions — means the county someone was actually browsing
  // right before they signed up survives into the picker instead of being
  // discarded. Watchlist still wins when present: it's a deliberate save,
  // a stronger signal than merely having a tab open.
  //
  // #2086 item 3: that `countyId` fallback can itself be a county with zero
  // live inventory (whatever the localStorage default or last-browsed
  // county happened to be) — the picker would then suggest, and the user
  // would pick, a county that lands them on an empty screen. When there's
  // no watchlist signal AND the currently-loaded county's real total is
  // known to be zero (fetch settled, not just "hasn't loaded yet"), suggest
  // DEFAULT_COUNTY_ID (Mecklenburg) instead — the flagship county with the
  // deepest real coverage. Watchlist and a genuinely non-empty current
  // county both still win over this; this only replaces a fallback that was
  // already a guess.
  const suggestedCounty = useMemo<string | undefined>(() => {
    const counts = new Map<string, number>();
    for (const entry of watchEntries) {
      const cid = entry.property?.countyId;
      if (!cid) continue;
      counts.set(cid, (counts.get(cid) || 0) + 1);
    }
    let best: string | undefined;
    let bestCount = 0;
    counts.forEach((count, cid) => {
      if (count > bestCount) {
        best = cid;
        bestCount = count;
      }
    });
    // No real signal (no deep-linked county, nothing watched): suggest
    // nothing. The map's current county is just whatever loaded first, and a
    // preselected pick locks for 180 days.
    if (!requestedCounty && !best) return undefined;
    return resolveSuggestedCounty({
      requestedCounty,
      bestWatchlistCounty: best,
      countyId,
      defaultCountyId: DEFAULT_COUNTY_ID,
      currentCountySettled: !loading && !error,
      currentCountyTotal: totalPropertyCount,
    });
  }, [requestedCounty, watchEntries, countyId, loading, error, totalPropertyCount]);

  // Open the county picker for a free/scout user who has never SAVED a pick.
  // counties_onboarded_at is now stamped only on save (it used to be stamped
  // when the modal mounted, so one Skip ended onboarding for good). A skip
  // snoozes it (lib/onboardingFlow) and the "Pick your free county" banner
  // below stays up meanwhile.
  useEffect(() => {
    if (
      shouldAutoOpenCountyPicker({
        signedIn: !!user,
        planLoaded,
        profileLoaded: countiesProfileLoaded,
        plan: userPlan,
        countiesOnboardedAt,
        chosenCounties,
        snoozed: user ? isCountyPickerSnoozed(user.id) : false,
        dismissedThisSession: pickerDismissedThisSession,
      }) &&
      // The questionnaire goes first; its last step opens the picker.
      !(onboardingRequired && !onboardingBypassed && userPlan === "free")
    ) {
      setShowCountyPicker(true);
    }
  }, [
    user,
    planLoaded,
    countiesProfileLoaded,
    userPlan,
    countiesOnboardedAt,
    chosenCounties,
    pickerDismissedThisSession,
    onboardingRequired,
    onboardingBypassed,
  ]);

  useEffect(() => {
    setPickBannerDismissed(user ? isPickCountyBannerDismissed(user.id) : false);
  }, [user]);

  // A free/scout user with no chosen county sees every card fuzzed; their
  // free county, not a paid plan, is the fix, so the fuzzed CTAs open the
  // picker for them (PropertyCard/PropertyModal/PropertyDetail onPickCounty).
  const needsCountyPick =
    planLoaded &&
    countiesProfileLoaded &&
    (userPlan === "free" || userPlan === "scout") &&
    chosenCounties.length === 0;
  const openCountyPicker = useCallback(() => setShowCountyPicker(true), []);
  const showPickCountyBanner = shouldShowPickCountyBanner({
    planLoaded,
    profileLoaded: countiesProfileLoaded,
    plan: userPlan,
    chosenCounties,
    dismissed: pickBannerDismissed,
    pickerOpen: showCountyPicker,
  });

  // In watchlist view, restrict the set of properties to watched ones.
  // Only that view depends on the watched set, so in every other view a watch
  // toggle no longer hands the filters, the sort and the map a new array.
  const watchlistIds = viewMode === "watchlist" ? watchedIds : null;
  const visibleProperties = useMemo(() => {
    if (watchlistIds) {
      return properties.filter((p) => watchlistIds.has(p.id));
    }
    if (!showHidden && hiddenIds.size > 0) {
      return properties.filter((p) => !hiddenIds.has(p.id));
    }
    return properties;
  }, [properties, watchlistIds, showHidden, hiddenIds]);
  // How many of this county's loaded properties the user has hidden.
  const hiddenCount = useMemo(
    () => (hiddenIds.size === 0 ? 0 : properties.filter((p) => hiddenIds.has(p.id)).length),
    [properties, hiddenIds]
  );

  // What the map draws. Once the full list has loaded this is exactly
  // visibleProperties, as before. While it is still loading, the map is drawn
  // from the slim pin payload instead (useCountyProperties), so the first
  // paint of the map no longer waits on the whole county. Watchlist view
  // narrows the pins to watched ids the same way it narrows the rows.
  // Nothing else reads this: the list, filters, counts and banners all stay on
  // the full rows.
  const mapPins = useMemo(() => {
    if (!loading) return visibleProperties;
    if (watchlistIds) return mapPoints.filter((p) => watchlistIds.has(p.id));
    if (!showHidden && hiddenIds.size > 0) return mapPoints.filter((p) => !hiddenIds.has(p.id));
    return mapPoints;
  }, [loading, visibleProperties, mapPoints, watchlistIds, showHidden, hiddenIds]);

  // Discovery-filtered set (no map cluster focus, no sort). The map's pin
  // filter reads THIS, not filteredProperties: a cluster focus narrows only
  // the list pane, never hides the rest of the map's pins (it used to, which
  // left the map showing 2-5 pins until the user found "Clear").
  // Stage per watched property, built only while the watchlist's stage filter
  // is on, so the filter memo below doesn't re-run on every watchlist change.
  const watchStageOf = useMemo(
    () =>
      viewMode === "watchlist" && watchlistStage
        ? new Map(watchEntries.map((e) => [e.propertyId, e.stage ?? null]))
        : null,
    [viewMode, watchlistStage, watchEntries]
  );
  const discoveryFiltered = useMemo(() => {
    let result = visibleProperties;
    let buyBoxStats = { missingKept: 0, missingExcluded: 0, outsideExcluded: 0 };
    // Discovery filters (status/class/auction/lead-type/vacant/search) only
    // apply on the All Properties tab (2026-09 tab-chrome redesign) — they're
    // the All Properties bar's own state, and Watchlist/Portfolio/Leads no
    // longer render that bar. Gating here (not just hiding the bar) matters:
    // without it, a filter left set from browsing would keep silently
    // narrowing the watchlist with no visible control explaining why.
    if (viewMode === "all") {
      // Filter on the DISPLAYED status (resolveDisplayStatus re-resolves
      // auction_scheduled/auction_passed from the upset-window close_date) so the
      // filter agrees with the card/map. Filtering raw p.status hid upgraded rows
      // (e.g. 326 Kirby: raw auction_passed + future close shows as scheduled).
      if (statusFilter) result = result.filter((p) => resolveDisplayStatus(p) === statusFilter);
      if (propertyClassFilter) result = result.filter((p) => p.propertyClass === propertyClassFilter);
      // Auction vs lead: inAuction is treated as true when absent (older
      // payloads/fixtures), matching resolvePropertyBadge's default.
      if (inAuctionFilter === "auction") result = result.filter((p) => p.inAuction !== false);
      else if (inAuctionFilter === "lead") result = result.filter((p) => p.inAuction === false);
      if (leadTypeFilter.length > 0) {
        result = result.filter((p) => (p.leadSignals || []).some((s) => leadTypeFilter.includes(s.signalType)));
      }
      if (vacantOnly) result = result.filter((p) => isVacantLand(p));
      if (changedOnly) result = result.filter((p) => changeKinds.has(p.id));
      if (ownerTypeFilter.length > 0) {
        // A row whose owner can't be read (gated, or no mailing data) is kept
        // while the buy box's "include missing data" switch is on.
        result = result.filter((p) => {
          const m = matchOwnerTypes(p, ownerTypeFilter);
          return m === "match" || (m === "unknown" && mapBuyBox.includeUnknown);
        });
      }
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        result = result.filter(
          (p) =>
            p.address?.toLowerCase().includes(q) ||
            p.parcelId?.toLowerCase().includes(q) ||
            p.city?.toLowerCase().includes(q)
        );
      }
      // Buy box last, so its missing-data counts describe the rows the other
      // filters already kept.
      const bb = applyMapBuyBox(result, mapBuyBox, isAllowedCounty);
      result = bb.matched;
      buyBoxStats = { missingKept: bb.missingKept, missingExcluded: bb.missingExcluded, outsideExcluded: bb.outsideExcluded };
    }
    if (watchStageOf) {
      result = result.filter((p) =>
        watchlistStage === "none" ? !watchStageOf.get(p.id) : watchStageOf.get(p.id) === watchlistStage
      );
    }
    if (viewMode === "watchlist" && watchlistQuery) {
      // Watchlist's own lightweight search-within-saved-set — no discovery
      // filters, just an address/parcel/city match over the (already
      // watched-only) visibleProperties.
      const q = watchlistQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.address?.toLowerCase().includes(q) ||
          p.parcelId?.toLowerCase().includes(q) ||
          p.city?.toLowerCase().includes(q)
      );
    }
    return { rows: result, buyBoxStats };
  }, [
    visibleProperties,
    viewMode,
    statusFilter,
    propertyClassFilter,
    inAuctionFilter,
    leadTypeFilter,
    changedOnly,
    changeKinds,
    ownerTypeFilter,
    vacantOnly,
    searchQuery,
    watchlistQuery,
    watchlistStage,
    watchStageOf,
    mapBuyBox,
    isAllowedCounty,
  ]);
  const discoveryFilteredProperties = discoveryFiltered.rows;
  const mapBuyBoxStats = discoveryFiltered.buyBoxStats;
  const mapBuyBoxActive = mapBuyBoxActiveCount(mapBuyBox) > 0;

  // One map_buybox_applied per settled edit (#3061 usage review at M3), not
  // one per keystroke.
  useEffect(() => {
    if (!mapBuyBoxActive) return;
    const t = window.setTimeout(() => {
      capture("map_buybox_applied", {
        county_id: countyId,
        criteria_count: mapBuyBoxActiveCount(mapBuyBox),
        include_unknown: mapBuyBox.includeUnknown,
      });
    }, 1500);
    return () => window.clearTimeout(t);
  }, [mapBuyBox, mapBuyBoxActive, countyId]);

  const filteredProperties = useMemo(() => {
    let result = discoveryFilteredProperties;
    // Map cluster focus (co-located pins the map can't separate by zooming)
    // narrows the list to exactly those properties.
    if (mapFocusIds) {
      const focusSet = new Set(mapFocusIds);
      result = result.filter((p) => focusSet.has(p.id));
    }
    if (mapArea) result = filterToArea(result, mapArea);

    // Apply sorting (field + direction — comparator + missing-value handling
    // live in lib/propertySort; score sort sinks terminal rows there).
    result = sortProperties(result, sortField, sortReversed);

    return result;
  }, [discoveryFilteredProperties, mapFocusIds, mapArea, sortField, sortReversed]);

  // A filter/search is active if ANY of these narrow the dataset. When
  // active but the filter matches zero rows, highlightedIds is [] — the
  // map must receive isFiltering=true so it renders no markers (rather
  // than falling back to "show everything").
  const isFiltering =
    !!searchQuery ||
    !!statusFilter ||
    !!propertyClassFilter ||
    !!inAuctionFilter ||
    leadTypeFilter.length > 0 ||
    changedOnly ||
    ownerTypeFilter.length > 0 ||
    vacantOnly ||
    mapArea !== null ||
    (viewMode === "all" && mapBuyBoxActive) ||
    viewMode !== "all";

  // Auto-clear the map cluster focus whenever the user changes any other
  // filter, search, county, or view. Centralizing the reset here (rather
  // than in every setter) means a cluster focus can never get "stuck"
  // contradicting the visible filter UI. Does not fire when only
  // mapFocusIds itself changes (it's not in the dep list), so setting a
  // focus from a map click survives.
  useEffect(() => {
    setMapFocusIds(null);
  }, [searchQuery, watchlistQuery, statusFilter, propertyClassFilter, inAuctionFilter, leadTypeFilter, changedOnly, ownerTypeFilter, vacantOnly, mapBuyBox, viewMode, countyId]);

  // An area belongs to one county and one view.
  useEffect(() => {
    setMapArea(null);
  }, [viewMode, countyId]);
  // So does a bulk selection (#3079).
  useEffect(() => {
    setSelectMode(false);
    setCheckedIds(new Set());
  }, [viewMode, countyId]);
  const handleAreaChange = useCallback(
    (a: MapArea | null) => {
      setMapArea(a);
      setMapFocusIds(null);
      if (a) capture("map_area_search", { mode: a.kind, county_id: countyId });
    },
    [countyId]
  );

  // Site-visit route (#3076): the watchlist as currently filtered and
  // sorted, as Google Maps driving links of up to 10 stops each.
  const watchRouteStops = useMemo(
    () =>
      viewMode === "watchlist"
        ? filteredProperties.map((p) => ({ lat: p.latitude, lng: p.longitude }))
        : [],
    [viewMode, filteredProperties]
  );
  const watchRouteLinks = useMemo(() => routeLinks(watchRouteStops), [watchRouteStops]);
  const watchRoutableCount = useMemo(() => routableCount(watchRouteStops), [watchRouteStops]);

  const highlightedIds = useMemo<string[]>(() => {
    if (!isFiltering) return [];
    // An area narrows the pins too (#3078); a cluster focus deliberately
    // narrows only the list (see discoveryFilteredProperties).
    const rows = mapArea ? filterToArea(discoveryFilteredProperties, mapArea) : discoveryFilteredProperties;
    return rows.map((p) => p.id);
  }, [isFiltering, discoveryFilteredProperties, mapArea]);

  const handleSelectProperty = useCallback(
    (property: Property | null) => {
      setSelectedProperty(property);
      // Read location from ref so this callback stays stable across URL
      // changes (avoids busting React.memo on ForeclosureMap). The ref
      // is kept in sync at the top of the render body.
      const loc = locationRef.current;
      if (property) {
        capture("property_opened", {
          property_id: property.id,
          county_id: property.countyId,
          status: property.status,
        });
        listRef.current?.scrollToCard(property.id);
        // Sync to URL for shareable links. PUSH (not replace) on the first
        // open so the device/browser back button has a modal entry to pop
        // (#501); REPLACE when switching between properties so we never
        // stack more than one modal entry.
        const nav = planOpenNav(loc.pathname, loc.search, property.id, didPushModalRef.current);
        navigate(nav.url, { replace: nav.kind === "replace" });
        if (nav.kind === "push") didPushModalRef.current = true;
      } else {
        // Closing. If we pushed a modal entry, pop it (navigate(-1)) so a
        // subsequent back leaves the map; otherwise strip the param in
        // place (deep-link open had no pushed entry).
        const nav = planCloseNav(loc.pathname, loc.search, didPushModalRef.current);
        if (nav.kind === "back") {
          didPushModalRef.current = false;
          navigate(-1);
        } else {
          navigate(nav.url, { replace: true });
        }
      }
    },
    [navigate]
  );

  // Map cluster click (#map-cluster): focus the list pane on the clicked
  // cluster's member properties. Deselect any open modal first so the
  // user lands on the narrowed list. The map is NOT narrowed — every pin
  // stays visible (map.tsx only calls this for co-located pins that zooming
  // can't separate; separable clusters just zoom to fit).
  const handleClusterSelect = useCallback(
    (ids: string[]) => {
      if (ids.length === 0) return;
      handleSelectProperty(null); // closes modal + clears the ?property= param
      setMapFocusIds(ids);
    },
    [handleSelectProperty]
  );

  // Handle shareable / deep-link property param ?property=<id>.
  //
  // Must open the modal even when the property ISN'T in the local list — which
  // happens for an emailed deep link or a map-pin click on the free tier (the
  // API caps how many rows we hold). The old code hard-returned on an empty/
  // capped list and only opened on a local match, so shared links to capped-out
  // rows silently never opened. Now: try the local list first (instant), else
  // fetch the row directly. The modal renders independently of `properties`
  // (see handleCrossCountySelect), so this is safe.
  const deepLinkFetchRef = useRef<string | null>(null);
  useEffect(() => {
    const propId = new URLSearchParams(location.search).get("property");
    if (!propId) {
      deepLinkFetchRef.current = null;
      return;
    }
    // Hard wall (#617): a deep-linked property requires a free account. The
    // ?property= param persists, so after a same-tab sign-in this effect
    // reruns (now with `user`) and opens the exact property. Signup's email
    // confirmation opens a NEW tab without the param, so the property is also
    // saved as a pending intent (lib/pendingIntent) and resumed there.
    //
    // Which tab: alert/digest emails go to existing accounts, so a bare deep
    // link opens "Sign in" (it used to open "Create account", sending
    // returning users into a signup form). Marketing links (ref=share, a
    // campaign utm_source, auth=signup) keep "Create account".
    if (!user) {
      if (authLoading) return;
      setAuthMode(deepLinkAuthMode(location.search));
      setShowAuth(true);
      if (isValidPropertyId(propId)) savePendingIntent({ intent: { kind: "property", propertyId: propId } });
      return;
    }
    if (selectedProperty?.id === propId) return; // already open
    const match = properties.find((p) => p.id === propId);
    if (match) {
      setSelectedProperty(match);
      setTimeout(() => listRef.current?.scrollToCard(match.id), 100);
      return;
    }
    // Not in the loaded set (free-tier cap, still loading, or another county)
    // → fetch directly. Dedupe so a given id is fetched once.
    if (deepLinkFetchRef.current === propId) return;
    deepLinkFetchRef.current = propId;
    getProperty(propId)
      .then((p) => {
        // A locked teaser has to go to the locked state, not Property state
        // (#1872) — the endpoint returns either shape.
        if (isLockedProperty(p)) setSelectedLocked(p);
        else setSelectedProperty(p);
      })
      .catch(() => {
        /* invalid / removed id — leave the map as-is */
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties, location.search, selectedProperty, user, authLoading]);

  // Pricing/share CTAs redirect to /?auth=signup — open the auth modal when
  // that param is present so the signup CTA isn't a dead end (it was ignored).
  //
  // ?intent= (Pricing's "pro:annual", Redeem's "redeem:<code>") is saved as a
  // pending intent here, when the modal opens, because the confirmation tab
  // will not have it (lib/pendingIntent.ts). A county landing page link
  // (?county=<id>) also opens signup, rather than the generic landing, and
  // carries the county across confirmation the same way.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const auth = params.get("auth");
    const county = params.get("county");
    if (!user && !authLoading) {
      if (isValidCountySlug(county)) savePendingIntent({ countyId: county });
      if (auth === "signup" || auth === "login") {
        const intent = parseIntentParam(params.get("intent"));
        if (intent) savePendingIntent({ intent });
        setAuthMode(auth === "signup" ? "signup" : "signin");
        setShowAuth(true);
      } else if (isValidCountySlug(county) && !params.has("property")) {
        setAuthMode("signup");
        setShowAuth(true);
      }
    } else if (user) {
      // Once logged in, never leave the auth modal stuck open over the app —
      // it used to persist from a ?auth param seen during the loading window
      // and re-appear on refresh over the working app (#617).
      setShowAuth(false);
    }
  }, [location.search, user, authLoading]);

  // Close the property modal when the ?property= param disappears from the
  // URL via a browser/device back navigation (#501). On Android, pressing
  // hardware back pops the pushed modal entry; React Router updates
  // `location`, and we sync state here so the modal closes (first back) and
  // the map remains (second back). Idempotent: when an explicit close
  // already cleared the param + state, the guard below makes this a no-op.
  useEffect(() => {
    const hasProp = new URLSearchParams(location.search).has("property");
    if (!hasProp && selectedProperty) {
      didPushModalRef.current = false;
      setSelectedProperty(null);
    }
  }, [location.search, selectedProperty]);

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
    setSelectedProperty(null);
  }, []);

  // Wrap setCountyId so user choices persist across reloads.
  //
  // County-lock (#864 follow-up, live-testing feedback 2026-08-09): Free
  // sees full data in exactly 1 county, Scout in up to 3 — but until now
  // the switcher itself stayed unrestricted, so anyone could browse any
  // county's full list (blurred fields aside). That's a bigger breadth
  // leak than the field-level blur was ever meant to allow: "give exactly
  // what we say we give." Free/Scout can now only switch among
  // chosenCounties; Pro/team/admin unaffected. Guarded on
  // `chosenCounties.length > 0` so a user who hit "Skip for now" on
  // CountyPickerModal (chosenCounties still []) isn't stranded on their
  // current county with literally nothing selectable — Settings → "Change
  // county" is the correct fix for that case, not a silent app lockup.
  const handleCountyChange = useCallback(
    (newId: string) => {
      if (!isUnrestrictedPlan && chosenCounties.length > 0 && !chosenCounties.includes(newId)) {
        return;
      }
      setCountyId(newId);
      try { localStorage.setItem(COUNTY_STORAGE_KEY, newId); } catch { /* ignore */ }
    },
    [isUnrestrictedPlan, chosenCounties]
  );

  // Cross-county search handler (#244): when an autocomplete result lives
  // in a different county than the one currently selected, switch the
  // CountySelector first so map.tsx flies the map and the property fetch
  // pulls the right county's rows. Then open the modal as usual.
  const handleCrossCountySelect = useCallback(
    (property: PropertyRow) => {
      if (property.countyId !== countyId) {
        handleCountyChange(property.countyId);
      }
      // A search hit can be a locked teaser outside the viewer's subset
      // (#2378) — same shape-split as the map-pin click and deep-link
      // paths just above. Still switch county first so the map flies to
      // where the property actually is; only the "open" step branches.
      if (isLockedProperty(property)) {
        setSelectedLocked(property);
        return;
      }
      // selectedProperty is independent of `properties`, so the modal
      // will render correctly even while the post-county-switch fetch
      // is in flight (PropertyModal closes over its own copy of the
      // property prop).
      handleSelectProperty(property);
    },
    [countyId, handleCountyChange, handleSelectProperty]
  );

  const handleRequireAuth = useCallback(() => setShowAuth(true), []);
  // onRequirePro is shared by two distinct triggers below: the watchlist
  // "at limit" star (PropertyCard/PropertyModal's WatchlistToggle) and the
  // "outside your area" fuzzed-data CTA (PropertyCard/PropertyModal/
  // PropertyDetail, gated on isAllowedCounty). The message below is
  // deliberately generic enough to cover either.
  const handleRequirePro = useCallback((code?: UpgradeReasonCode) => {
    // Defensive guard: never show the upgrade prompt to a truly
    // unrestricted plan (pro/team/admin) even if a stale component state
    // accidentally triggers it (e.g. race on the profile fetch).
    //
    // AUDIT (#677): this checks `isUnrestrictedPlan`, NOT `isPro`. Scout is
    // a paid tier but still has real caps (watchlist=10 via watchLimitMax,
    // county=3 via isAllowedCounty) and must be able to reach this prompt
    // when it hits either one. Before #677 this checked `isPro` (which
    // includes scout), so a Scout user hitting the watchlist cap or
    // clicking "upgrade" on an out-of-county fuzzed card would silently
    // hit a dead end — the callback no-op'd, no modal, no message. That
    // became reachable when Scout went on sale (the Scout flip).
    if (isUnrestrictedPlan) return;
    openUpgrade(
      code ?? "watchlist_cap",
      `Your plan is limited to ${watchLimitMax} watched properties and full data in your chosen counties. Upgrade for unlimited watchlisting and full coverage everywhere.`
    );
  }, [isUnrestrictedPlan, watchLimitMax, openUpgrade]);

  const handleBulkWatch = useCallback(async () => {
    const wanted = bulkIds().filter((id) => !watchedIds.has(id));
    if (!wanted.length) {
      finishBulk("watch", 0);
      return;
    }
    const room = Math.max(0, watchLimitMax - watchEntries.length);
    if (room === 0) {
      handleRequirePro("watchlist_cap");
      return;
    }
    setBulkBusy(true);
    try {
      const added = await addManyToWatchlist(wanted.slice(0, room));
      finishBulk("watch", added);
      if (wanted.length > room) handleRequirePro("watchlist_cap");
      else toast(`Added ${added} to your watchlist.`, "success");
    } finally {
      setBulkBusy(false);
    }
  }, [bulkIds, watchedIds, watchLimitMax, watchEntries.length, addManyToWatchlist, finishBulk, handleRequirePro, toast]);
  const handleBulkHide = useCallback(async () => {
    const ids = bulkIds();
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      await hideMany(ids);
      finishBulk("hide", ids.length);
    } finally {
      setBulkBusy(false);
    }
  }, [bulkIds, hideMany, finishBulk]);
  const handleBulkStage = useCallback(
    async (stage: DealStage | null) => {
      const ids = bulkIds().filter((id) => watchedIds.has(id));
      if (!ids.length) return;
      setBulkBusy(true);
      try {
        for (const id of ids) await setStage(id, stage);
        finishBulk("stage", ids.length);
      } finally {
        setBulkBusy(false);
      }
    },
    [bulkIds, watchedIds, setStage, finishBulk]
  );

  // Hard wall (#617): the product (map / list / property detail) requires a
  // free account. While Supabase resolves the session, show a neutral branded
  // splash so the app shell never flashes before auth resolves (#497). Then:
  // logged-out visitors get the public landing + the auth modal and the app
  // shell never mounts (no anonymous data fetch); logged-in users get the app.
  // Marketing pages (/pricing, /public, the SEO guides) are SEPARATE routes
  // and stay public — only this Dashboard (the app product) is walled.
  if (authLoading) {
    return (
      <div className="app-splash" role="status" aria-label="Loading Parcely">
        <div className="landing-logo app-splash-logo">
          Parcel<span>y</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <>
        <LandingPage
          onSignIn={() => { setAuthMode("signin"); setShowAuth(true); }}
          onGetStarted={() => { setAuthMode("signup"); setShowAuth(true); }}
        />
        {showAuth && <AuthModal initialMode={authMode} onClose={() => setShowAuth(false)} />}
        <FeedbackButton />
      </>
    );
  }

  return (
    <>
      {/* <main> landmark (#211 mechanical slice, mirroring LandingPage.tsx:97):
          swap the app-shell div for <main>, keeping .app's flex layout. Adds a
          screen-reader "main" landmark without CSS or DOM restructuring. */}
      {/* Skip link: first focusable element in the app shell (#211). */}
      <a className="skip-link" href="#main">Skip to main content</a>
      <main id="main" tabIndex={-1} className="app" style={{ display: "flex" }}>
        {/* Dashboard has no h1 of its own; LeadsView renders its own, so only
           add the sr-only one on the other views to avoid two h1s per page. */}
        {viewMode !== "leads" && <h1 className="sr-only">Parcely: NC foreclosure and distressed property map</h1>}
        {/* Brand/chrome row: logo + tabs + auth (2026-09 tab-chrome
            redesign). Tabs read as "where you are" on the identity row,
            not another filter row below the search bar. Only the logo
            navigates home — the row itself is not a link (the
            whole-header-clickable bug lost users' context on a stray
            click). */}
        <div className="app-brandbar">
          <a
            href="/"
            className="landing-logo"
            style={{ textDecoration: "none" }}
            aria-label="Parcely home"
          >
            Parcel<span>y</span>
          </a>
          <div className="view-tabs">
            <button
              className={`view-tab${viewMode === "all" ? " view-tab--active" : ""}`}
              onClick={() => setViewMode("all")}
            >
              All Properties
            </button>
            <button
              className={`view-tab${viewMode === "watchlist" ? " view-tab--active" : ""}`}
              onClick={() => {
                if (!authed) {
                  setShowAuth(true);
                  return;
                }
                setViewMode("watchlist");
              }}
            >
              My Watchlist{authed && watchEntries.length > 0 ? ` (${watchEntries.length})` : ""}
            </button>
            <button
              className={`view-tab${viewMode === "portfolio" ? " view-tab--active" : ""}`}
              data-tour="tab-portfolio"
              onClick={() => {
                // Portfolio tab is Pro + 3-watched gated. Route unauthed
                // users to sign-in, free users to upgrade, and show an
                // inline empty state when they're Pro but haven't built
                // a portfolio yet.
                //
                // AUDIT (#677): `isPro` here (includes scout) is deliberate,
                // not an over-grant — Portfolio Analytics isn't mentioned in
                // Pricing.tsx's TIERS copy for any tier (it predates that
                // copy), and there's no per-tier variation built for it.
                // The upgrade-prompt copy below says "Pro feature" loosely
                // (means "paid feature"); Scout is meant to pass this gate.
                if (!authed) {
                  setShowAuth(true);
                  return;
                }
                if (!isPro) {
                  openUpgrade(
                    "portfolio_tab",
                    "Portfolio Analytics is included with Scout and Pro — track aggregate stats, auction timelines, and equity distribution across your watched properties."
                  );
                  return;
                }
                setViewMode("portfolio");
              }}
              title={
                authed && isPro && watchEntries.length < PORTFOLIO_MIN_WATCHED
                  ? `Add ${PORTFOLIO_MIN_WATCHED - watchEntries.length} more to your watchlist to unlock`
                  : undefined
              }
            >
              Portfolio
              {!isPro && <span className="view-tab-lock" aria-hidden="true">Scout+</span>}
            </button>
            <button
              className={`view-tab${viewMode === "leads" ? " view-tab--active" : ""}`}
              data-tour="tab-leads"
              onClick={() => {
                // Leads is Scout+ gated (full rows in Scout's chosen counties,
                // statewide for pro/team/admin). Route unauthed users to sign-in
                // and free users to upgrade; the backend enforces the same.
                if (!authed) {
                  setShowAuth(true);
                  return;
                }
                if (!canUseLeads) {
                  openUpgrade(
                    "leads_tab",
                    "Seller Leads is a paid feature — pre-foreclosure owner names, mailing addresses, equity estimates, and CSV export for direct outreach."
                  );
                  return;
                }
                setViewMode("leads");
              }}
            >
              Leads
              {!canUseLeads && <span className="view-tab-lock" aria-hidden="true">Scout+</span>}
            </button>
          </div>

          <div className="top-bar-auth">
            {authed && canUseLeads && (
              <NotificationBell
                onOpenLeads={(boxId) => {
                  setLeadsBoxRequest(boxId ? { id: boxId, n: Date.now() } : undefined);
                  setViewMode("leads");
                }}
              />
            )}
            {authed ? (
              <UserMenu
                onOpenSettings={() => setShowSettings(true)}
                onUpgrade={() => openUpgrade("user_menu")}
                isPro={isPro}
                isAdmin={userPlan === "admin"}
                onManageSubscription={openBillingPortal}
                onBookSetupCall={() => setSetupCallSource("user_menu")}
                onReplayTour={() => setTourReplayNonce((n) => n + 1)}
              />
            ) : !authLoading ? (
              <button className="signin-btn" onClick={() => setShowAuth(true)}>
                Sign In
              </button>
            ) : null}
          </div>
        </div>

        {/* Per-tab work bar (2026-09 tab-chrome redesign): county scope +
            whichever filters actually apply to the ACTIVE tab, instead of
            one generic discovery bar every tab saw regardless of
            relevance. Leads renders nothing extra here — LeadsView owns
            its whole bar (search/Filed range/buy box), and stacking this
            bar above it was the original "why does the leads tab show the
            main search stuff" complaint. Portfolio renders no bar at all:
            PortfolioView aggregates across every watched county, so a
            single-county selector here would misrepresent what it's
            showing. */}
        {viewMode !== "portfolio" && (
          <div className="tab-context-bar">
            <CountySelector
              value={countyId}
              onChange={handleCountyChange}
              // An EMPTY chosenCounties must pass `undefined` (unrestricted),
              // not `[]`. `[]` means "restrict to nothing", which correctly
              // hides the selector — but an empty array here means "this user
              // hasn't picked counties yet" (skipped onboarding, or the
              // profile fetch hasn't resolved), and those users still need to
              // navigate. Passing `[]` removed the county switcher entirely
              // for them. Same "don't strand them" guard handleCountyChange
              // uses, and it matches the backend's AllowsListScope, which also
              // treats an empty chosen set as "no restriction to apply".
              restrictTo={
                isUnrestrictedPlan || chosenCounties.length === 0 ? undefined : chosenCounties
              }
            />

            {viewMode === "all" && (
              <SearchAutocomplete
                currentCountyId={countyId}
                onSearchChange={handleSearchChange}
                onSelectProperty={handleCrossCountySelect}
                statusFilter={statusFilter}
                onStatusFilterChange={(s) => {
                  capture("filter_changed", { filter_type: "status", value: s || "all" });
                  setStatusFilter(s);
                }}
                propertyClassFilter={propertyClassFilter}
                onPropertyClassFilterChange={(c) => {
                  capture("filter_changed", { filter_type: "class", value: c || "all" });
                  setPropertyClassFilter(c);
                }}
                inAuctionFilter={inAuctionFilter}
                onInAuctionFilterChange={(v) => {
                  capture("filter_changed", { filter_type: "in_auction", value: v || "all" });
                  setInAuctionFilter(v);
                  if (v === "auction") setLeadTypeFilter([]); // lead type is meaningless for auctions
                }}
                leadTypeFilter={leadTypeFilter}
                ownerTypeFilter={ownerTypeFilter}
                onOwnerTypeFilterChange={(t) => {
                  capture("filter_changed", { filter_type: "owner_type", value: t.length ? t.join(",") : "all" });
                  setOwnerTypeFilter(t);
                }}
                onLeadTypeFilterChange={(t) => {
                  capture("filter_changed", { filter_type: "lead_type", value: t.length ? t.join(",") : "all" });
                  setLeadTypeFilter(t);
                }}
                vacantOnly={vacantOnly}
                onVacantOnlyChange={(v) => {
                  capture("filter_changed", { filter_type: "vacant_only", value: v ? "true" : "false" });
                  setVacantOnly(v);
                }}
                buyBox={mapBuyBox}
                onBuyBoxChange={setMapBuyBox}
                buyBoxMatchCount={discoveryFilteredProperties.length}
                buyBoxStats={mapBuyBoxStats}
                buyBoxSaved={
                  authed && canUseLeads && countyId
                    ? {
                        countyId,
                        countyName: activeCounty?.name,
                        savedLimit: leadsTier === "scout" ? SCOUT_BUY_BOX_LIMIT : undefined,
                      }
                    : undefined
                }
                availableLeadTypes={availableLeadTypes}
                onExportCsv={isPro ? handleExportCsv : undefined}
              />
            )}

            {viewMode === "watchlist" && authed && (
              <FollowUpsDue
                onOpenProperty={(id) => {
                  const p = properties.find((x) => x.id === id);
                  if (p) handleSelectProperty(p);
                }}
              />
            )}

            {viewMode === "watchlist" && (
              // My Watchlist's own light bar: search within the saved set,
              // nothing else — a handful of watched properties doesn't need
              // Statuses/Classes/Auctions+Leads/Lead Types. Sort still lives
              // on PropertyList's list header below, same as All Properties.
              <div className="saved-set-search search-autocomplete">
                <div className="search-input-wrap">
                  <input
                    type="text"
                    className="search-input"
                    placeholder={`Search ${watchEntries.length} saved ${watchEntries.length === 1 ? "property" : "properties"}...`}
                    aria-label="Search your watchlist"
                    value={watchlistQuery}
                    onChange={(e) => setWatchlistQuery(e.target.value)}
                  />
                  {watchlistQuery && (
                    <button
                      className="search-clear-btn"
                      onClick={() => setWatchlistQuery("")}
                      aria-label="Clear"
                    >
                      ×
                    </button>
                  )}
                </div>
                <select
                  className="deal-stage-filter"
                  aria-label="Filter by deal stage"
                  value={watchlistStage}
                  onChange={(e) => setWatchlistStage(e.target.value)}
                >
                  <option value="">All stages</option>
                  {DEAL_STAGES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                  <option value="none">No stage</option>
                </select>
                {watchRouteLinks.length > 0 && (
                  <div className="watch-route" role="group" aria-label="Driving route">
                    {watchRouteLinks.map((href, i) => (
                      <a
                        key={href}
                        className="watch-route-link"
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() =>
                          capture("route_opened", { stops: watchRoutableCount, leg: i + 1, legs: watchRouteLinks.length })
                        }
                        title="Open a Google Maps driving route through these properties, in list order"
                      >
                        {watchRouteLinks.length === 1
                          ? `Route ${watchRoutableCount} ${watchRoutableCount === 1 ? "stop" : "stops"}`
                          : `Route stops ${i * STOPS_PER_LINK + 1}–${Math.min((i + 1) * STOPS_PER_LINK, watchRoutableCount)}`}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Failed subscription payment: grace deadline, then paused access.
            Keyed by user so it re-reads billing state after a sign-in. */}
        {user && <PaymentIssueBanner key={user.id} />}

        {/* Persistent "pick your free county" nudge while a free/scout user
            has no chosen county — including everyone the old mount-time
            stamp marked onboarded without a pick. Dismissible per session. */}
        {showPickCountyBanner && (
          <div className="free-tier-banner pick-county-banner" role="status">
            <span>
              {userPlan === "scout"
                ? "Pick your counties (up to 3) to unlock full owner and property detail where you invest."
                : "Pick your free county to unlock full owner and property detail — value, equity, owner info — where you invest."}
            </span>
            <button className="signin-btn" onClick={openCountyPicker}>
              {userPlan === "scout" ? "Pick counties" : "Pick your free county"}
            </button>
            <button
              type="button"
              className="banner-dismiss"
              aria-label="Dismiss"
              onClick={() => {
                if (user) dismissPickCountyBanner(user.id);
                setPickBannerDismissed(true);
              }}
            >
              ×
            </button>
          </div>
        )}

        {billingNotice && (
          <div className="free-tier-banner" role="status">
            <span>{billingNotice}</span>
            <button type="button" className="banner-dismiss" aria-label="Dismiss" onClick={() => setBillingNotice(null)}>
              ×
            </button>
          </div>
        )}

        {/* "X of Y unlocked" — the honest version of the old banner. Gated
            to All Properties (2026-09 tab-chrome redesign) — it's about
            THIS county's browse inventory, meaningless on the other three
            tabs. Previously this said "Showing X of Y", which was true
            when the backend simply returned fewer rows. Now the locked
            rows ARE on screen (grey, on the map), so the accurate claim is
            about what's unlocked, not what's shown. */}
        {viewMode === "all" && freeTierLimit > 0 && totalPropertyCount > freeTierLimit && (
          <div className="free-tier-banner">
            <span>
              <strong>{freeTierLimit}</strong> of{" "}
              <strong>{totalPropertyCount}</strong> properties unlocked in this
              county.{" "}
              {lockedProperties.length > 0 && (
                <>
                  <strong>{lockedProperties.length}</strong> more are shown
                  locked on the map.
                </>
              )}
            </span>
            <button
              className="signin-btn"
              onClick={() =>
                openUpgrade(
                  "county_subset_banner",
                  `Your plan unlocks ${freeTierLimit} of the ${totalPropertyCount} properties in this county. Upgrade to open every one.`
                )
              }
            >
              Upgrade to Pro
            </button>
          </div>
        )}

        {/* Thin-county notice (#1378). Mutually exclusive with the
            unlock banner above — this fires only when nothing is locked
            (freeTierLimit >= totalPropertyCount, so that banner's
            condition is false) but the county's real inventory is just
            small. Without this, a county with 1-9 properties shows a
            handful of cards and then silence, with nothing distinguishing
            "this county is genuinely thin" from "something broke". Also
            gated to All Properties — same reasoning as the banner above. */}
        {viewMode === "all" &&
          !(freeTierLimit > 0 && totalPropertyCount > freeTierLimit) &&
          totalPropertyCount > 0 &&
          totalPropertyCount <= SMALL_COUNTY_THRESHOLD && (
            <div className="free-tier-banner">
              <span>
                Only <strong>{totalPropertyCount}</strong>{" "}
                {totalPropertyCount === 1 ? "property is" : "properties are"}{" "}
                tracked in this county right now — inventory varies widely
                across NC's counties.{" "}
                {isUnrestrictedPlan
                  ? "Check back soon, or try a different county."
                  : "Try a different county, or upgrade to Pro for full access to every NC county at once."}
              </span>
            </div>
          )}

        {/* Zero-inventory notice (#2086 item 3). Distinct from the
            thin-county notice above, which requires totalPropertyCount > 0
            — before this, a county with GENUINELY ZERO properties fell
            through both banners and the tour/flow could land a brand-new
            user on a bare list pane with no cards and no explanation.
            Suggests DEFAULT_COUNTY_ID (Mecklenburg) — the flagship county
            with the deepest real coverage — as "what's nearby" rather than
            true geographic adjacency, which the frontend has no data for.
            Only offers the switch button when it would actually work:
            unrestricted plans, a free/scout user who hasn't picked yet
            (chosenCounties empty — handleCountyChange allows any county
            then), or a free/scout user whose picked set already includes
            Mecklenburg. Otherwise a free/scout user locked to a different,
            empty county can't switch away without waiting out the 180-day
            cadence gate or upgrading — same fallback the thin-county
            notice above already uses for that case. */}
        {viewMode === "all" && !loading && !error && totalPropertyCount === 0 && (() => {
          const notice = getEmptyCountyNotice({
            countyId,
            defaultCountyId: DEFAULT_COUNTY_ID,
            isUnrestrictedPlan,
            chosenCounties,
          });
          return (
            <div className="free-tier-banner">
              <span>
                No properties are tracked in <strong>{activeCounty?.name ?? "this county"}</strong>{" "}
                right now — inventory varies widely across NC's counties. {notice.tail}
              </span>
              {notice.canSwitchToDefault && (
                <button className="signin-btn" onClick={() => handleCountyChange(DEFAULT_COUNTY_ID)}>
                  Browse Mecklenburg
                </button>
              )}
            </div>
          );
        })()}

        {viewMode === "portfolio" ? (
          /* Portfolio takes the whole content area — replaces the
             map+list split pane. The guard above already ensures
             authed + isPro before we get here; PortfolioView itself
             handles the "no watched properties" empty state. */
          <Suspense fallback={null}>
            <PortfolioView entries={watchEntries} />
          </Suspense>
        ) : viewMode === "leads" ? (
          /* Seller-leads table — takes the whole content area. The tab guard
             above ensures authed + Pro before we get here. */
          <LeadsView countyId={countyId} tier={leadsTier} boxRequest={leadsBoxRequest} />
        ) : (
        <div className="split-pane">
          <div className="list-pane">
            {loading && <SkeletonCard count={10} />}
            {error && (
              /* Human copy from lib/apiErrors (never axios's "Request
                 failed with status code …"), plus the one action that helps. */
              <div className="list-error" role="alert">
                <p>{error}</p>
                {(() => {
                  const kind = propertiesErrorInfo?.kind;
                  const ownCounty = (propertiesErrorInfo?.chosenCounties ?? chosenCounties).find((id) => id !== countyId);
                  const ownCountyName = ownCounty
                    ? counties.find((c) => c.id === ownCounty)?.name ?? ownCounty
                    : null;
                  const seePlans = (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() =>
                        openUpgrade(
                          kind === "county_not_in_plan" ? "county_not_in_plan" : "unknown",
                          kind === "county_not_in_plan"
                            ? "Your plan covers the counties you picked. Upgrade to Pro for every NC county."
                            : undefined
                        )
                      }
                    >
                      See plans
                    </button>
                  );
                  if (kind === "county_not_in_plan") {
                    return (
                      <div className="list-error__actions">
                        {ownCounty ? (
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => handleCountyChange(ownCounty)}
                          >
                            Switch to {ownCountyName}
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => setShowCountyPicker(true)}
                          >
                            Choose your county
                          </button>
                        )}
                        {seePlans}
                      </div>
                    );
                  }
                  if (kind === "forbidden") {
                    return <div className="list-error__actions">{seePlans}</div>;
                  }
                  return (
                    <div className="list-error__actions">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setPropertiesReloadKey((k) => k + 1)}
                      >
                        Try again
                      </button>
                    </div>
                  );
                })()}
              </div>
            )}
            {!loading && !error && (
              <div className="list-pane__scroll">
                {auctionSheetOpen && (
                  <AuctionSheet
                    properties={filteredProperties}
                    watchedIds={watchedIds}
                    countyName={activeCounty?.name}
                    onClose={() => setAuctionSheetOpen(false)}
                    onPrint={(rows) => capture("auction_sheet_printed", { rows, county_id: countyId })}
                  />
                )}
                {selectMode && (
                  <BulkActionBar
                    count={checkedIds.size}
                    selectableCount={Math.min(filteredProperties.length, BULK_MAX)}
                    busy={bulkBusy}
                    onSelectAll={() =>
                      setCheckedIds(new Set(filteredProperties.slice(0, BULK_MAX).map((p) => p.id)))
                    }
                    onClear={() => setCheckedIds(new Set())}
                    onDone={() => {
                      setSelectMode(false);
                      setCheckedIds(new Set());
                    }}
                    onWatch={handleBulkWatch}
                    onHide={handleBulkHide}
                    onSetStage={viewMode === "watchlist" ? handleBulkStage : undefined}
                    onExport={isPro && viewMode === "all" ? handleBulkExport : undefined}
                  />
                )}
                {mapArea !== null && (
                  <div className="map-focus-banner" role="status">
                    <span className="map-focus-banner__label">
                      Showing {filteredProperties.length}{" "}
                      {filteredProperties.length === 1 ? "property" : "properties"} in the{" "}
                      {mapArea.kind === "polygon" ? "area you drew" : "area you searched"}
                    </span>
                    <button type="button" className="map-focus-banner__clear" onClick={() => setMapArea(null)}>
                      Clear
                    </button>
                  </div>
                )}
                {mapFocusIds !== null && (
                  <div className="map-focus-banner" role="status">
                    <span className="map-focus-banner__label">
                      Showing {filteredProperties.length}{" "}
                      {filteredProperties.length === 1 ? "property" : "properties"} from map
                    </span>
                    <button
                      type="button"
                      className="map-focus-banner__clear"
                      onClick={() => setMapFocusIds(null)}
                    >
                      Clear
                    </button>
                  </div>
                )}
                <PropertyList
                  ref={listRef}
                  allProperties={visibleProperties}
                  filteredProperties={filteredProperties}
                  selectedProperty={selectedProperty}
                  onSelectProperty={handleSelectProperty}
                  authed={authed}
                  isAllowedCounty={isAllowedCounty}
                  watchedIds={watchedIds}
                  togglingIds={togglingIds}
                  watchLimit={watchEntries.length}
                  watchLimitMax={watchLimitMax}
                  onToggleWatch={toggleWatch}
                  hiddenIds={hiddenIds}
                  onToggleHidden={toggleHidden}
                  stageLabels={viewMode === "watchlist" ? stageLabels : undefined}
                  changeKinds={viewMode === "all" ? changeKinds : undefined}
                  changedCount={viewMode === "all" ? changesCount : 0}
                  changedOnly={viewMode === "all" && changedOnly}
                  onToggleChangedOnly={viewMode === "all" ? () => setChangedOnly((v) => !v) : undefined}
                  hoverStore={hoverStore}
                  selectMode={selectMode}
                  checkedIds={checkedIds}
                  onToggleChecked={toggleChecked}
                  onOpenAuctionSheet={
                    viewMode === "all" || viewMode === "watchlist" ? () => setAuctionSheetOpen(true) : undefined
                  }
                  onToggleSelectMode={
                    authed
                      ? () => {
                          setSelectMode((v) => !v);
                          setCheckedIds(new Set());
                        }
                      : undefined
                  }
                  hiddenCount={viewMode === "all" ? hiddenCount : 0}
                  showHidden={showHidden}
                  onToggleShowHidden={() => setShowHidden((v) => !v)}
                  onRequireAuth={handleRequireAuth}
                  onRequirePro={handleRequirePro}
                  onPickCounty={needsCountyPick ? openCountyPicker : undefined}
                  sortField={sortField}
                  sortFields={SORT_FIELDS}
                  onSortFieldChange={(f) => {
                    setSortField(f as SortField);
                    setSortReversed(false);
                  }}
                  sortDirectional={SORT_FIELDS.find((x) => x.value === sortField)?.directional ?? false}
                  sortReversed={sortReversed}
                  sortDirTooltip={sortDirTooltip(sortField, sortReversed)}
                  onToggleSortReversed={() => setSortReversed((r) => !r)}
                  activeLeadTypeFilter={leadTypeFilter.length ? leadTypeFilter : undefined}
                  countLabelOverride={
                    viewMode === "watchlist" && visibleProperties.length === 0
                      ? "No watched properties yet"
                      : undefined
                  }
                  emptyStateOverride={
                    viewMode === "watchlist" && visibleProperties.length === 0
                      ? {
                          icon: "⭐",
                          title: "Your watchlist is empty",
                          desc:
                            "Star any property in the All Properties view to track it here. " +
                            "Watched properties trigger email alerts when new upset bids land " +
                            "or auction dates change.",
                          cta: {
                            label: "Browse all properties",
                            onClick: () => setViewMode("all"),
                          },
                        }
                      : undefined
                  }
                />
              </div>
            )}
            {/* Data-source footer (#audit-9). Trust signal at the bottom
                of the list pane: "where is this data from + how fresh".
                Always visible regardless of view mode. */}
            <DataSourceFooter />
          </div>

          <div className="map-pane">
            {/* Inner boundary: if ArcGIS basemap tile-load fails or any
                map-internal error occurs, fall back gracefully without
                nuking the list pane on the left. */}
            <ErrorBoundary
              boundary="map"
              fallback={
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  color: "var(--text-muted)",
                  padding: 24,
                  textAlign: "center",
                }}>
                  Map failed to load. Reload the page to try again — the
                  property list on the left still works.
                </div>
              }
            >
              <Suspense fallback={
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  color: "var(--text-muted)",
                }}>
                  Loading map…
                </div>
              }>
              <ForeclosureMap
                allProperties={mapPins}
                highlightedIds={highlightedIds}
                isFiltering={isFiltering}
                onSelectProperty={handleSelectProperty}
                onClusterSelect={handleClusterSelect}
                selectedProperty={selectedProperty}
                county={activeCounty}
                lockedProperties={loading ? mapLockedProperties : lockedProperties}
                onSelectLocked={setSelectedLocked}
                hoverStore={hoverStore}
                area={mapArea}
                onAreaChange={handleAreaChange}
              />
              </Suspense>
            </ErrorBoundary>
          </div>
        </div>
        )}

        {selectedProperty && (
          <PropertyModal
            property={selectedProperty}
            onClose={() => handleSelectProperty(null)}
            authed={authed}
            isPro={isPro}
            isAllowedCounty={isAllowedCounty(selectedProperty)}
            isWatched={watchedIds.has(selectedProperty.id)}
            watchLoading={togglingIds.has(selectedProperty.id)}
            watchLimit={watchEntries.length}
            watchLimitMax={watchLimitMax}
            onToggleWatch={toggleWatch}
            isHidden={hiddenIds.has(selectedProperty.id)}
            onToggleHidden={toggleHidden}
            onRequireAuth={handleRequireAuth}
            onRequirePro={handleRequirePro}
            onPickCounty={needsCountyPick ? openCountyPicker : undefined}
            getNotes={getNotes}
            updateNotes={updateNotes}
            getStage={getStage}
            setStage={setStage}
            activeLeadTypeFilter={leadTypeFilter.length ? leadTypeFilter : undefined}
          />
        )}
        {showSettings && (
          <SettingsPanel
            isPro={isPro}
            userPlan={userPlan}
            onClose={() => setShowSettings(false)}
            onChangeCounty={() => {
              setShowSettings(false);
              setShowCountyPicker(true);
            }}
            onDisplayPreferenceChange={() => {
              // The resolved-rows toggle changes what the server returns for
              // every county, so no cached county is valid any more.
              clearPropertiesCache();
              setPropertiesReloadKey((k) => k + 1);
            }}
            onNoSubscription={() => {
              setShowSettings(false);
              showNoSubscription();
            }}
          />
        )}
        {showUpgrade && hasHigherPlan && (
          <UpgradePrompt
            onClose={() => setShowUpgrade(false)}
            reason={upgradeReason}
            reasonCode={upgradeReasonCode}
            plan={userPlan}
            onRequireAuth={() => {
              pendingUpgrade.current = true;
              setShowUpgrade(false);
              setShowAuth(true);
            }}
          />
        )}
        {resumeCheckout && hasHigherPlan && (
          <ResumeCheckoutPrompt
            plan={resumeCheckout.plan}
            interval={resumeCheckout.interval}
            variant={resumeCheckout.variant}
            onClose={() => setResumeCheckout(null)}
          />
        )}
        {/* The tour used to render on top of the county picker (independent
            conditions); it now waits for the picker and the attribution
            prompt to be out of the way. */}
        {authed && user && (
          <ProductTour
            userId={user.id}
            plan={userPlan}
            enabled={!loading && properties.length > 0 && !showCountyPicker && !showAttribution && !showOnboarding}
            currentView={viewMode}
            onNavigate={setViewMode}
            onUpgrade={() => openUpgrade("user_menu")}
            replayNonce={tourReplayNonce}
          />
        )}
        {showOnboarding && (
          <OnboardingQuestionnaire
            askSource={signupSourceAskedAt === null}
            onDone={() => {
              setOnboardingRequired(false);
              setSignupSourceAskedAt(new Date().toISOString());
              // The zip is unlocked server-side now; refetch the list.
              setPropertiesReloadKey((k) => k + 1);
            }}
            onContinueWithoutSaving={() => setOnboardingBypassed(true)}
            // The zip step needs a county; the picker hides this modal while
            // it is open, so a snoozed picker can no longer strand the user.
            needsCounty={needsCountyPick}
            onPickCounty={openCountyPicker}
            suspended={showCountyPicker}
          />
        )}
        {setupCallSource && <SetupCallModal source={setupCallSource} onClose={() => setSetupCallSource(null)} />}
        {showAttribution && (
          <AttributionPrompt onDone={() => setSignupSourceAskedAt(new Date().toISOString())} />
        )}
        {/* One-time county picker (geography-aware tier redesign, #864). Only
            free/scout participate — the effect above never sets
            showCountyPicker for pro/team/admin. What's picked here now also
            drives the county SWITCHER above (handleCountyChange) — free/
            scout can only navigate among these counties, not just see
            blurred data outside them. See handleCountyChange's comment. */}
        {showCountyPicker && (userPlan === "free" || userPlan === "scout") && (
          <CountyPickerModal
            plan={userPlan}
            suggestedCounty={suggestedCounty}
            currentCounties={chosenCounties}
            onClose={() => {
              // A dismiss is a snooze, not "onboarded": nothing is stamped.
              // Keep it shut for this page session; a user with no county
              // gets it again on a later visit (and the banner meanwhile).
              setPickerDismissedThisSession(true);
              setShowCountyPicker(false);
              if (user && chosenCounties.length === 0) snoozeCountyPicker(user.id);
            }}
            onSaved={(counties) => {
              // Chosen counties decide which rows come back unlocked, so the
              // cached responses are stale — clear them and refetch.
              clearPropertiesCache();
              setPropertiesReloadKey((k) => k + 1);
              setChosenCounties(counties);
              setCountiesOnboardedAt((prev) => prev ?? new Date().toISOString());
              setRequestedCounty(null);
              clearCountyPickerSnooze();
              setShowCountyPicker(false);
            }}
          />
        )}
      </main>
      {/* This branch only renders for a logged-in user (the !user wall
          early-returns above with its own AuthModal). A logged-in user never
          needs the auth modal, so guard on !user — that keeps a stale
          showAuth (e.g. from a ?auth param seen mid-load) from popping the
          modal over the working app (#617). */}
      {showAuth && !user && <AuthModal onClose={() => setShowAuth(false)} />}
      {/* Locked-property teaser. Opened only by clicking a grey map dot —
          it never appears in the list, because locked rows never reach it. */}
      {selectedLocked && (
        <LockedPropertyCard
          property={selectedLocked}
          onClose={() => setSelectedLocked(null)}
          onUpgrade={() => {
            setSelectedLocked(null);
            openUpgrade(
              "locked_property",
              "This property isn't unlocked on your plan. Upgrade to see its address, value, equity and full auction timeline."
            );
          }}
        />
      )}
      <FeedbackButton />
    </>
  );
}

export default Dashboard;
